import { continueFromImport, defaultSettings, GYM_PRESETS, importEvents, ImportError, mass, parseHistory, toDisplay, validateSettings, type EventOf, type Gym, type MainLift, type Settings, type Unit, type WorkoutKind } from '@repwise/domain';
import { uuidv7 } from '@repwise/sync';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GymEditor } from '../components/GymEditor.tsx';
import { InstallCard } from '../components/InstallCard.tsx';
import { readText, RestoreBackup } from '../components/RestoreBackup.tsx';
import { Button } from '../components/ui.tsx';
import { LIFT_NAME } from '../lib/defaults.ts';
import { useRepo } from '../lib/hooks.ts';

const LIFTS: MainLift[] = ['squat', 'bench', 'row', 'ohp', 'deadlift'];
/** Official starting weights (research/01 R4; DL 40 / Row 30 per G1). */
const START: Record<Unit, Record<MainLift, number>> = {
  kg: { squat: 20, bench: 20, row: 30, ohp: 20, deadlift: 40 },
  lb: { squat: 45, bench: 45, row: 65, ohp: 45, deadlift: 95 },
};

type Step = 'units' | 'experience' | 'weights' | 'gym' | 'accessories' | 'ready';
const ORDER: Step[] = ['units', 'experience', 'weights', 'gym', 'accessories', 'ready'];
/** OB08: choosing another programme asks the unit and the gym here; the plan's own setup (PR01–PR04) does the rest. */
const PICK_ORDER: Step[] = ['units', 'experience', 'gym'];

/** J1 steps 4–9. Nothing is written until "Start Workout A", so quitting halfway leaves no partial programme. */
export function Onboarding() {
  const { store } = useRepo();
  const [step, setStep] = useState<Step>('units');
  const [unit, setUnit] = useState<Unit>('kg');
  const [continuing, setContinuing] = useState(false);
  const [weights, setWeights] = useState<Record<MainLift, number>>(START.kg);
  const [gym, setGym] = useState<Gym>(GYM_PRESETS.standard_kg.gym);
  const [curls, setCurls] = useState(false);
  const [pushups, setPushups] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const navigate = useNavigate();
  const [bringing, setBringing] = useState<'stronglifts' | 'backup' | null>(null);
  /** StrongLifts history chosen during onboarding: written with the programme, continued from (ADR-0017). */
  const [imported, setImported] = useState<{ events: EventOf<'workout_imported'>[]; lastAt: number; nextKind: WorkoutKind | null; daysSince?: number; breakFraction?: number } | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const chooseStrongLifts = async (f: File) => {
    try {
      const parsed = parseHistory(await readText(f), { unit });
      const now = Date.now();
      const evs = importEvents([], parsed, unit, uuidv7, now).events;
      const settings = defaultSettings(unit);
      const s = continueFromImport(evs, { ...settings, gym }, undefined, now, Intl.DateTimeFormat().resolvedOptions().timeZone);
      if (!s) throw new ImportError('empty', 'This file has no workouts in it.');
      setWeights((w) => ({ ...w, ...Object.fromEntries(s.lifts.map((l) => [l.lift, toDisplay(l.next)])) }));
      setImported({ events: evs, lastAt: s.lastAt, nextKind: s.nextKind, ...(s.breakFraction ? { daysSince: s.daysSince!, breakFraction: s.breakFraction } : {}) });
      setContinuing(true);
      setImportError(null);
      setBringing(null);
      next();
    } catch (e) {
      setImportError(e instanceof ImportError ? e.message : "This file couldn't be read.");
    }
  };
  const order = picking ? PICK_ORDER : ORDER;
  const idx = order.indexOf(step);
  const next = () => setStep(order[idx + 1]!);
  const back = () => (step === 'gym' && picking ? (setPicking(false), setStep('experience')) : setStep(order[Math.max(0, idx - 1)]!));
  const inc = unit === 'kg' ? 2.5 : 5;
  const firstKind: WorkoutKind = imported?.nextKind ?? 'A';

  const chooseUnit = (u: Unit) => {
    setUnit(u);
    setWeights(START[u]);
    setGym(u === 'kg' ? GYM_PRESETS.standard_kg.gym : GYM_PRESETS.standard_lb.gym);
    next();
  };

  const finish = (toCatalogue = false) => {
    const base = defaultSettings(unit);
    const settings: Settings = { ...base, gym, curl: { ...base.curl, enabled: curls }, pushup: { ...base.pushup, enabled: pushups } };
    const errs = validateSettings(settings);
    if (errs.length) return setError(errs[0]!.message);
    const now = Date.now();
    // OB08: the built-in 5×5 is the base a hosted plan applies on top of; leaving the catalogue keeps it.
    if (toCatalogue) navigate('/settings/programme?welcome=1');
    void store.append([
      { type: 'programme_started', id: uuidv7(now), at: now, unit, gym, startWeights: Object.fromEntries(LIFTS.map((l) => [l, mass(weights[l])])) as Record<MainLift, number>, curl: { weight: mass(unit === 'kg' ? 10 : 20) }, pushup: { sets: 3, reps: 10 } },
      { type: 'settings_updated', id: uuidv7(now + 1), at: now + 1, settings },
      ...(imported ? imported.events : []),
      ...(imported?.nextKind === 'B' ? [{ type: 'next_workout_set' as const, id: uuidv7(now + 2), at: now + 2, kind: 'B' as const }] : []),
    ]);
  };

  return (
    <main className="min-h-full p-5 pb-36 max-w-[480px] mx-auto">
      <div className="flex items-center justify-between h-11">
        {idx > 0 ? (
          <button type="button" className="text-text-muted min-h-11 pr-3" onClick={back}>
            Back
          </button>
        ) : (
          <span />
        )}
        <span className="text-sm text-text-muted" aria-label={`Step ${idx + 1} of ${order.length + (picking ? 1 : 0)}`}>
          {idx + 1} of {order.length + (picking ? 1 : 0)}
        </span>
      </div>

      {step === 'units' && (
        <>
          <h1 className="text-title num mt-4">Kilograms or pounds?</h1>
          <p className="text-text-muted mt-2">Your plates and every target use this unit.</p>
          <div className="mt-6 space-y-3">
            <Button onClick={() => chooseUnit('kg')}>Kilograms (kg)</Button>
            <Button kind="secondary" onClick={() => chooseUnit('lb')}>
              Pounds (lb)
            </Button>
          </div>
        </>
      )}

      {step === 'experience' && (
        <>
          <h1 className="text-title num mt-4">Where are you starting from?</h1>
          <div className="mt-6 space-y-3">
            <Option title="New to 5×5: use the starting weights" body="StrongLifts 5×5, three days a week. Start light and add weight every time you get all your reps." onClick={() => (setContinuing(false), setWeights(START[unit]), next())} />
            <Option title="Already training: enter my current weights" body="Carry on with StrongLifts 5×5 from the weights you lift now." onClick={() => (setContinuing(true), next())} />
            <Option title="Coming from StrongLifts, Strong or Hevy: import my history" body="Choose the CSV your old app exports. Your next weights come from your last workout there." expanded={bringing === 'stronglifts'} onClick={() => setBringing(bringing === 'stronglifts' ? null : 'stronglifts')} />
            {bringing === 'stronglifts' && (
              <div className="bg-surface-raised rounded-[14px] p-3 space-y-2">
                <p className="text-sm">Export a CSV from your old app (StrongLifts: Settings → Export Data; Strong: Settings → Export Strong Data; Hevy: Settings → Export &amp; Import Data), then choose it here. Your next weights come from your last workout there.</p>
                <label className="block text-sm">
                  Workout history CSV file
                  <input type="file" accept=".csv,text/csv,text/comma-separated-values" className="block mt-1 min-h-12" onChange={(e) => e.target.files?.[0] && void chooseStrongLifts(e.target.files[0])} />
                </label>
                {importError && (
                  <p className="text-miss text-sm" role="alert">
                    {importError}
                  </p>
                )}
              </div>
            )}
            <Option title="Choose a different programme" body="Madcow 5×5, StrongLifts 5×5 Ultra, Intermediate and more." onClick={() => (setPicking(true), setContinuing(false), setWeights(START[unit]), setStep('gym'))} />
            <Button kind="text" onClick={() => setBringing(bringing === 'backup' ? null : 'backup')}>
              Restore a Repwise backup
            </Button>
            {bringing === 'backup' && (
              <div className="bg-surface-raised rounded-[14px] p-3">
                <RestoreBackup />
              </div>
            )}
          </div>
        </>
      )}

      {step === 'weights' && (
        <>
          <h1 className="text-title num mt-4">{continuing ? 'Your current weights' : 'Starting weights'}</h1>
          {imported && (
            <p className="text-sm mt-2" role="status">
              {imported.events.length} imported workouts ready to add. These weights continue from your last one, on {new Date(imported.lastAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })}.
              {imported.breakFraction ? ` That was ${imported.daysSince} days ago, so they start ${Math.round(imported.breakFraction * 100)}% lighter to ease back in.` : ''}
            </p>
          )}
          <p className="text-text-muted mt-2">{continuing ? 'The weight you lift next for each exercise.' : 'Starting light is part of the programme. The weight goes up every time you get all your reps.'}</p>
          <ul className="space-y-2 mt-5">
            {LIFTS.map((l) => (
              <li key={l} className="flex items-center justify-between bg-surface-raised rounded-[14px] p-3">
                <span>{LIFT_NAME[l]}</span>
                <span className="flex items-center gap-3">
                  <button type="button" className="min-w-11 min-h-11 rounded-full border border-outline" onClick={() => setWeights((w) => ({ ...w, [l]: Math.max(gym.bar / 100, +(w[l] - inc).toFixed(2)) }))} aria-label={`Lower ${LIFT_NAME[l]}`}>
                    −
                  </button>
                  <span className="num text-num-l w-24 text-center">
                    {weights[l]} {unit}
                  </span>
                  <button type="button" className="min-w-11 min-h-11 rounded-full border border-outline" onClick={() => setWeights((w) => ({ ...w, [l]: +(w[l] + inc).toFixed(2) }))} aria-label={`Raise ${LIFT_NAME[l]}`}>
                    +
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      {step === 'gym' && (
        <>
          <h1 className="text-title num mt-4">Your gym</h1>
          <div className="flex gap-2 mt-4 flex-wrap">
            {(unit === 'kg' ? (['standard_kg', 'home_kg'] as const) : (['standard_lb'] as const)).map((k) => (
              <button key={k} type="button" className="min-h-11 px-3 rounded-full border border-outline text-sm" onClick={() => setGym(GYM_PRESETS[k].gym)}>
                {GYM_PRESETS[k].label}
              </button>
            ))}
          </div>
          <div className="mt-5">
            <GymEditor gym={gym} unit={unit} onChange={setGym} />
          </div>
        </>
      )}

      {step === 'accessories' && (
        <>
          <h1 className="text-title num mt-4">Extras after the main lifts</h1>
          <p className="text-text-muted mt-2">Optional. These are your own additions, not part of standard 5×5. You can change them any time.</p>
          <div className="mt-5 space-y-2">
            {[
              { on: curls, set: setCurls, label: 'Dumbbell curls', hint: '3 × 10; a heavier dumbbell when you hit every rep' },
              { on: pushups, set: setPushups, label: 'Push-ups', hint: '3 × 10; one more rep each time, then an extra set' },
            ].map((x) => (
              <label key={x.label} className="flex items-center justify-between bg-surface-raised rounded-[14px] p-3 min-h-14">
                <span>
                  {x.label}
                  <span className="block text-xs text-text-muted">{x.hint}</span>
                </span>
                <input type="checkbox" className="w-6 h-6 accent-[var(--now-ink)]" checked={x.on} onChange={(e) => x.set(e.target.checked)} />
              </label>
            ))}
          </div>
        </>
      )}

      {step === 'ready' && (
        <>
          <h1 className="text-title num mt-4">Workout {firstKind} is ready</h1>
          <p className="text-text-muted mt-2">Squat, bench press and barbell row. Warm-ups and plates are worked out for you.</p>
          <InstallCard />
          {error && (
            <p className="text-miss text-sm mt-3" role="alert">
              {error}
            </p>
          )}
        </>
      )}

      {step !== 'units' && step !== 'experience' && (
        <div className="fixed bottom-0 inset-x-0 p-5 bg-surface" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 20px)' }}>
          <div className="max-w-[480px] mx-auto">{step === 'ready' ? <Button onClick={() => finish()}>Go to Workout {firstKind}</Button> : picking && step === 'gym' ? <Button onClick={() => finish(true)}>Continue to programmes</Button> : <Button onClick={next}>Continue</Button>}</div>
        </div>
      )}
    </main>
  );
}

/** OB08 final (Wave 2.4 batch 2): a card per way to start; the title is the choice, the line under it what follows. */
function Option({ title, body, onClick, expanded }: { title: string; body: string; onClick: () => void; expanded?: boolean }) {
  return (
    <button type="button" className="w-full text-left bg-surface-raised rounded-[16px] p-4 min-h-11 active:scale-[0.99] transition-transform" onClick={onClick} aria-expanded={expanded}>
      <span className="block text-headline">{title}</span>
      <span className="block text-sm text-text-muted mt-1">{body}</span>
    </button>
  );
}
